import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, CalendarDays, Clock, Users, Banknote, Loader2, ArrowLeft, CheckCircle, CreditCard, User, Zap, XCircle, Share2, ReceiptText, Award } from "lucide-react";
import ShareDialog from "@/components/ShareDialog";
import { KycBookingNotice } from "@/components/KycBookingNotice";
import { getBookingKycMessage, hasApprovedBookingKyc } from "@/lib/bookingKyc";

const sportColors = { basketball: "sport-basketball", badminton: "sport-badminton", pickleball: "sport-pickleball" };

export default function EventDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [paymentChecking, setPaymentChecking] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const fetchEvent = useCallback(async () => {
    try {
      const res = await api.get(`/events/${id}`);
      setEvent(res.data);
    } catch { toast.error("Event not found"); navigate("/events"); }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => { fetchEvent(); }, [fetchEvent]);

  // Poll payment status if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const paymentParam = searchParams.get("payment");
    if (sessionId && paymentParam === "success") {
      setPaymentChecking(true);
      const poll = async (attempts = 0) => {
        if (attempts >= 6) { setPaymentChecking(false); return; }
        try {
          const res = await api.get(`/events/${id}/payment-status`, { params: { session_id: sessionId } });
          if (res.data.payment_status === "paid") {
            toast.success("Payment confirmed! You're registered.");
            setPaymentChecking(false);
            fetchEvent();
            return;
          }
        } catch {}
        setTimeout(() => poll(attempts + 1), 2000);
      };
      poll();
    }
  }, [searchParams, id, fetchEvent]);

  const handleRegister = async () => {
    if (!user) { navigate("/login"); return; }
    if (!hasApprovedBookingKyc(user)) {
      toast.error(getBookingKycMessage(user));
      navigate("/profile");
      return;
    }
    setRegistering(true);
    try {
      const res = await api.post(`/events/${id}/register`);
      if (res.data.status === "PENDING_PAYMENT") {
        // Redirect to Stripe
        const originUrl = window.location.origin;
        const checkout = await api.post(`/events/${id}/checkout`, { event_id: id, origin_url: originUrl });
        if (checkout.data.url) { window.location.href = checkout.data.url; return; }
      }
      toast.success("Registered successfully!");
      fetchEvent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    }
    setRegistering(false);
  };

  const handleCancelRegistration = async () => {
    setCancelling(true);
    try {
      await api.post(`/events/${id}/cancel-registration`);
      toast.success("Registration cancelled");
      setCancelDialog(false);
      fetchEvent();
    } catch (err) { toast.error(err.response?.data?.detail || "Cancel failed"); }
    setCancelling(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;
  if (!event) return null;

  const isFree = !event.price || event.price <= 0;
  const spotsLeft = event.spots_left ?? Math.max(0, event.capacity - (event.registered_count || 0));
  const isFull = spotsLeft <= 0;
  const myReg = event.registrations?.find(r => r.user_id === user?.user_id && r.status !== "CANCELLED");
  const isRegistered = !!myReg;
  const kycBlocked = user && !hasApprovedBookingKyc(user);
  const kycMessage = getBookingKycMessage(user);

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-6">
      <button data-testid="back-to-events" onClick={() => navigate("/events")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" />Back to Events
      </button>

      {paymentChecking && (
        <div className="bg-amber-50 border-2 border-amber-200 p-4 mb-6 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
          <p className="text-sm font-medium text-amber-800">Confirming your payment...</p>
        </div>
      )}

      {/* Hero Image */}
      <div className="relative h-52 md:h-72 overflow-hidden mb-6 border-b-4 border-r-4 border-black">
        <img src={event.image || "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=1000"} alt={event.title}
          className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6">
          <div className="flex gap-2 mb-2">
            <Badge className={`${sportColors[event.sport_type]} text-[10px] font-heading uppercase tracking-wider`}>{event.sport_type}</Badge>
            {isFree && <Badge className="bg-lime-volt text-black text-[10px] font-heading uppercase">Free</Badge>}
            {event.status === "CANCELLED" && <Badge className="bg-red-500 text-white text-[10px] font-heading uppercase">Cancelled</Badge>}
          </div>
          <h1 className="font-heading text-2xl md:text-4xl text-white font-bold uppercase tracking-tight" data-testid="event-title">{event.title}</h1>
        </div>
        <Button data-testid="share-event-btn" onClick={() => setShareOpen(true)}
          className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 text-white backdrop-blur-sm rounded-full w-10 h-10 p-0">
          <Share2 className="w-5 h-5" />
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Details */}
        <div className="lg:col-span-2 space-y-6">
          <div>
            <h2 className="font-heading text-lg uppercase tracking-tight font-bold mb-3">About This Event</h2>
            <p className="text-muted-foreground leading-relaxed">{event.description}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white border-2 border-slate-100 p-4">
              <CalendarDays className="w-5 h-5 text-court-orange mb-2" />
              <p className="font-heading text-sm uppercase tracking-wider text-muted-foreground">Date</p>
              <p className="font-heading text-lg font-bold">{event.event_date}</p>
            </div>
            <div className="bg-white border-2 border-slate-100 p-4">
              <Clock className="w-5 h-5 text-court-orange mb-2" />
              <p className="font-heading text-sm uppercase tracking-wider text-muted-foreground">Time</p>
              <p className="font-heading text-lg font-bold">{event.start_time} - {event.end_time}</p>
            </div>
            {event.venue_name && (
              <div className="bg-white border-2 border-slate-100 p-4 col-span-2">
                <MapPin className="w-5 h-5 text-court-orange mb-2" />
                <p className="font-heading text-sm uppercase tracking-wider text-muted-foreground">Venue</p>
                <p className="font-heading text-lg font-bold">{event.venue_name}</p>
                <p className="text-sm text-muted-foreground">{event.venue_address}, {event.venue_city}</p>
              </div>
            )}
          </div>

          {/* Coach Info */}
          {event.coach_name && (
            <div className="bg-white border-2 border-slate-100 p-4" data-testid="event-coach-info">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-court-orange/10 rounded-full flex items-center justify-center">
                  <Award className="w-5 h-5 text-court-orange" />
                </div>
                <div>
                  <p className="font-heading text-sm uppercase tracking-wider text-muted-foreground">Coach</p>
                  <p className="font-heading text-base font-bold">{event.coach_name}</p>
                </div>
              </div>
            </div>
          )}

          {/* Registered Players */}
          {event.registrations?.length > 0 && (
            <div>
              <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-3">
                Registered Players ({event.registrations.filter(r => r.status !== "CANCELLED").length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {event.registrations.filter(r => r.status !== "CANCELLED").map(r => (
                  <div key={r.registration_id} className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 text-sm">
                    <User className="w-3 h-3 text-muted-foreground" />
                    <span>{r.user_name}</span>
                    {r.status === "PAID" && <CheckCircle className="w-3 h-3 text-green-500" />}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: Action Card */}
        <div className="lg:col-span-1">
          <div className="bg-white border-2 border-slate-200 p-6 sticky top-20" data-testid="event-action-card">
            <div className="text-center mb-4">
              <p className="font-heading text-3xl font-bold text-court-orange">
                {isFree ? "FREE" : `₱${event.price?.toFixed(0)}`}
              </p>
              {!isFree && <p className="text-xs text-muted-foreground">per person</p>}
            </div>

            <Separator className="mb-4" />

            <div className="space-y-2 text-sm mb-6">
              <div className="flex justify-between"><span className="text-muted-foreground">Capacity</span><span className="font-medium">{event.capacity} players</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Registered</span><span className="font-medium">{event.registered_count || 0}</span></div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Spots Left</span>
                <span className={`font-heading font-bold ${spotsLeft <= 5 ? 'text-red-500' : 'text-lime-volt'}`}>{spotsLeft}</span>
              </div>
              <div className="flex justify-between"><span className="text-muted-foreground">Organizer</span><span className="font-medium">{event.organizer_name}</span></div>
              {event.coach_name && <div className="flex justify-between"><span className="text-muted-foreground">Coach</span><span className="font-medium">{event.coach_name}</span></div>}
              {event.event_type && <div className="flex justify-between"><span className="text-muted-foreground">Type</span><span className="font-medium capitalize">{event.event_type}</span></div>}
            </div>

            {event.status === "CANCELLED" ? (
              <div className="text-center p-4 bg-red-50 border border-red-200">
                <XCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
                <p className="font-heading text-sm uppercase text-red-600">Event Cancelled</p>
              </div>
            ) : isRegistered ? (
              <div className="space-y-3">
                <div className="text-center p-4 bg-green-50 border border-green-200">
                  <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <p className="font-heading text-sm uppercase text-green-700">You're Registered!</p>
                  <p className="text-[10px] text-muted-foreground mt-1">Status: {myReg.status}</p>
                </div>
                {myReg.status === "PAID" && event.price > 0 && (
                  <Button data-testid="event-receipt-btn" variant="outline" onClick={() => navigate(`/receipts/event/${myReg.registration_id}`)}
                    className="w-full rounded-full border-2 font-heading uppercase text-xs">
                    <ReceiptText className="w-3 h-3 mr-1" />View Receipt
                  </Button>
                )}
                <Button data-testid="cancel-registration-btn" variant="outline" onClick={() => setCancelDialog(true)}
                  className="w-full rounded-full border-2 border-red-200 text-red-500 hover:bg-red-50 font-heading uppercase text-xs">
                  <XCircle className="w-3 h-3 mr-1" />Cancel Registration
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {kycBlocked && <KycBookingNotice message={kycMessage} compact testId="event-kyc-notice" />}
                <Button data-testid="register-event-btn" onClick={handleRegister} disabled={registering || isFull || kycBlocked}
                  className="w-full bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide h-12 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:shadow-none disabled:opacity-50">
                  {registering ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> :
                   kycBlocked ? "KYC Approval Required" :
                   isFull ? "Event Full" :
                   isFree ? <><Zap className="w-4 h-4 mr-2" />Register Free</> :
                   <><CreditCard className="w-4 h-4 mr-2" />Register - ₱{event.price?.toFixed(0)}</>}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <DialogContent className="sm:max-w-sm" data-testid="cancel-reg-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">Cancel Registration?</DialogTitle>
            <DialogDescription>You'll lose your spot in this event.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => setCancelDialog(false)} className="flex-1 rounded-full border-2">Keep Spot</Button>
            <Button data-testid="confirm-cancel-reg-btn" onClick={handleCancelRegistration} disabled={cancelling}
              className="flex-1 bg-destructive hover:bg-destructive/90 text-white rounded-full font-heading uppercase">
              {cancelling ? "Cancelling..." : "Cancel It"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen}
        shareUrl={`${window.location.origin}/events/${id}`}
        title={event.title}
        description={`${event.sport_type} event on ${event.event_date} at ${event.start_time}${event.venue_name ? ` - ${event.venue_name}` : ''}. ${event.price > 0 ? `₱${event.price}` : 'FREE'}`} />
    </div>
  );
}